import { createFileRoute } from "@tanstack/react-router"
import * as React from "react"
import { flushSync } from "react-dom"
import { useQuery } from "@tanstack/react-query"
import { toPng } from "html-to-image"
import ExcelJS from "exceljs"
import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Line,
	LineChart,
	Tooltip,
	XAxis,
	YAxis,
	ResponsiveContainer,
} from "recharts"

export const Route = createFileRoute("/_protected/portal-cautivo")({
	component: PortalCautivoPage,
})

type Stats = {
	totalSesiones: number
	totalUsuarios: number
	porDia: { fecha: string; sesiones: number; usuarios: number }[]
	edades: { categoria: string; cantidad: number }[]
	generos: { categoria: string; cantidad: number }[]
}

const ABBREV_MAP: Record<string, string> = {
	"Hombre": "Hombre",
	"Mujer": "Mujer",
	"Mujer jefe de hogar": "M. Jefe hogar",
	"En condición de discapacidad": "Discapacidad",
	"Víctima": "Víctima",
	"Etnias": "Etnias",
	"Desmovilizado": "Desmovilizado",
	"OSIGD - LGBTI": "LGBTI",
}

async function captureChart(ref: React.RefObject<HTMLDivElement | null>): Promise<string | null> {
	if (!ref.current) return null
	try {
		return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" })
	} catch {
		return null
	}
}

async function downloadExcel(
	stats: Stats,
	fromDate: string,
	toDate: string,
	chartRefs: React.RefObject<HTMLDivElement | null>[],
) {
	const wb = new ExcelJS.Workbook()

	/* ── Hoja 1: Datos ── */
	const wsDatos = wb.addWorksheet("Datos")

	// Totales
	wsDatos.addRow(["Total Sesiones", stats.totalSesiones])
	wsDatos.addRow(["Total Usuarios", stats.totalUsuarios])
	wsDatos.addRow([])

	// Sesiones y usuarios por día
	wsDatos.addRow(["Fecha", "Sesiones", "Usuarios"])
	stats.porDia.forEach((r) => { wsDatos.addRow([r.fecha, r.sesiones, r.usuarios]) })
	wsDatos.addRow([])

	// Edades
	wsDatos.addRow(["Rango de Edad", "Cantidad"])
	stats.edades.forEach((r) => { wsDatos.addRow([r.categoria, r.cantidad]) })
	wsDatos.addRow([])

	// Géneros
	wsDatos.addRow(["Género", "Cantidad"])
	stats.generos.forEach((r) => { wsDatos.addRow([r.categoria, r.cantidad]) })

	// Estilo cabeceras
	const headerRows = [1, 2, 4, wsDatos.rowCount - stats.edades.length - stats.generos.length - 3, wsDatos.rowCount - stats.generos.length - 1]
		;[4, 4 + stats.porDia.length + 2, 4 + stats.porDia.length + 2 + stats.edades.length + 2].forEach((rowNum) => {
			const row = wsDatos.getRow(rowNum)
			row.font = { bold: true }
			row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } }
		})
	wsDatos.columns = [{ width: 28 }, { width: 14 }, { width: 14 }]

	/* ── Hoja 2: Estadísticas (imágenes de las gráficas) ── */
	const wsStats = wb.addWorksheet("Estadísticas")

	const titles = [
		"Sesiones y Usuarios por Día",
		"Usuarios por Rango de Edad",
		"Usuarios por Género",
	]

	let currentRow = 1
	for (let i = 0; i < chartRefs.length; i++) {
		const dataUrl = await captureChart(chartRefs[i])
		if (!dataUrl) continue

		// Título
		wsStats.getCell(`A${currentRow}`).value = titles[i]
		wsStats.getCell(`A${currentRow}`).font = { bold: true, size: 12 }
		currentRow++

		// Convertir base64 a buffer
		const base64 = dataUrl.replace(/^data:image\/png;base64,/, "")
		const imageId = wb.addImage({ base64, extension: "png" })

		const imgHeight = 200 // px aproximado
		const rowHeight = 15  // pts por fila
		const rowsNeeded = Math.ceil((imgHeight / rowHeight) * 1.5)

		wsStats.addImage(imageId, {
			tl: { col: 0, row: currentRow - 1 },
			ext: { width: 600, height: imgHeight },
		})

		// Ajustar alturas de filas para que quepa la imagen
		for (let r = currentRow; r < currentRow + rowsNeeded; r++) {
			wsStats.getRow(r).height = rowHeight
		}

		currentRow += rowsNeeded + 2
	}

	wsStats.getColumn(1).width = 90

	/* ── Descarga ── */
	const buffer = await wb.xlsx.writeBuffer()
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	})
	const url = URL.createObjectURL(blob)
	const a = document.createElement("a")
	a.href = url
	a.download = `portal-cautivo_${fromDate}_${toDate}.xlsx`
	a.click()
	URL.revokeObjectURL(url)
}

function sanitizeSheetName(name: string, used: Set<string>): string {
	const cleaned = name.replace(/[\\/?*[\]:]/g, "_").trim().slice(0, 31) || "Zona"
	let candidate = cleaned
	let i = 2
	while (used.has(candidate.toLowerCase())) {
		const suffix = `_${i++}`
		candidate = cleaned.slice(0, 31 - suffix.length) + suffix
	}
	used.add(candidate.toLowerCase())
	return candidate
}

const IMG_WIDTH = 520
const IMG_HEIGHT = 240
const IMG_ROW_SPAN = Math.ceil(IMG_HEIGHT / 15)

function addImageToSection(
	wb: ExcelJS.Workbook,
	ws: ExcelJS.Worksheet,
	dataUrl: string | null,
	titleRow: number,
	col: number,
) {
	if (!dataUrl) return
	const base64 = dataUrl.replace(/^data:image\/png;base64,/, "")
	const imageId = wb.addImage({ base64, extension: "png" })
	ws.addImage(imageId, {
		tl: { col, row: titleRow - 1 },
		ext: { width: IMG_WIDTH, height: IMG_HEIGHT },
	})
}

function padRows(ws: ExcelJS.Worksheet, untilRow: number) {
	while (ws.rowCount < untilRow) ws.addRow([])
}

async function downloadExcelPorZona(
	zonas: { item: string; municipio: string | null; nombre: string | null }[],
	fromDate: string,
	toDate: string,
	prepareCharts: (s: Stats) => Promise<void>,
	chartRefs: {
		dia: React.RefObject<HTMLDivElement | null>
		edad: React.RefObject<HTMLDivElement | null>
		genero: React.RefObject<HTMLDivElement | null>
	},
	onProgress: (done: number, total: number) => void,
) {
	const wb = new ExcelJS.Workbook()

	/* ── Resumen por zona ── */
	const wsResumen = wb.addWorksheet("Resumen")
	wsResumen.addRow(["Rango", `${fromDate} → ${toDate}`])
	wsResumen.addRow([])
	const headerRow = wsResumen.addRow(["Zona", "Municipio", "Nombre", "Total Sesiones", "Total Usuarios"])
	headerRow.font = { bold: true }
	headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } }
	wsResumen.columns = [{ width: 14 }, { width: 22 }, { width: 32 }, { width: 16 }, { width: 16 }]

	const usedNames = new Set<string>(["resumen"])
	let done = 0

	for (const z of zonas) {
		const params = new URLSearchParams({ from: fromDate, to: toDate, zona: z.item })
		const res = await fetch(`/api/portal-cautivo/stats?${params}`, { credentials: "include" })
		if (!res.ok) {
			done++
			onProgress(done, zonas.length)
			continue
		}
		const s = (await res.json()) as Stats

		wsResumen.addRow([z.item, z.municipio ?? "", z.nombre ?? "", s.totalSesiones, s.totalUsuarios])

		const sheetName = sanitizeSheetName(z.item, usedNames)
		const ws = wb.addWorksheet(sheetName)
		ws.columns = [{ width: 22 }, { width: 14 }, { width: 14 }]

		// Renderizar las gráficas para esta zona y capturarlas
		await prepareCharts(s)
		const [imgDia, imgEdad, imgGen] = await Promise.all([
			captureChart(chartRefs.dia),
			captureChart(chartRefs.edad),
			captureChart(chartRefs.genero),
		])

		// Cabecera
		ws.addRow(["Zona", z.item])
		if (z.municipio) ws.addRow(["Municipio", z.municipio])
		if (z.nombre) ws.addRow(["Nombre", z.nombre])
		ws.addRow(["Rango", `${fromDate} → ${toDate}`])
		ws.addRow([])
		ws.addRow(["Total Sesiones", s.totalSesiones])
		ws.addRow(["Total Usuarios", s.totalUsuarios])
		ws.addRow([])

		/* ── Sección: Sesiones y Usuarios por Día ── */
		const tDia = ws.addRow(["Sesiones y Usuarios por Día"])
		tDia.font = { bold: true, size: 12 }
		const titleDiaRow = tDia.number
		const hDia = ws.addRow(["Fecha", "Sesiones", "Usuarios"])
		hDia.font = { bold: true }
		hDia.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } }
		s.porDia.forEach((r) => { ws.addRow([r.fecha, r.sesiones, r.usuarios]) })
		addImageToSection(wb, ws, imgDia, titleDiaRow, 4)
		padRows(ws, titleDiaRow + IMG_ROW_SPAN)
		ws.addRow([])

		/* ── Sección: Edades ── */
		const tEdad = ws.addRow(["Usuarios por Rango de Edad"])
		tEdad.font = { bold: true, size: 12 }
		const titleEdadRow = tEdad.number
		const hEdad = ws.addRow(["Rango de Edad", "Cantidad"])
		hEdad.font = { bold: true }
		hEdad.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } }
		s.edades.forEach((r) => { ws.addRow([r.categoria, r.cantidad]) })
		addImageToSection(wb, ws, imgEdad, titleEdadRow, 4)
		padRows(ws, titleEdadRow + IMG_ROW_SPAN)
		ws.addRow([])

		/* ── Sección: Géneros ── */
		const tGen = ws.addRow(["Usuarios por Género"])
		tGen.font = { bold: true, size: 12 }
		const titleGenRow = tGen.number
		const hGen = ws.addRow(["Género", "Cantidad"])
		hGen.font = { bold: true }
		hGen.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8EAF6" } }
		s.generos.forEach((r) => { ws.addRow([r.categoria, r.cantidad]) })
		addImageToSection(wb, ws, imgGen, titleGenRow, 4)
		padRows(ws, titleGenRow + IMG_ROW_SPAN)

		done++
		onProgress(done, zonas.length)
	}

	const totalRow = wsResumen.rowCount + 1
	wsResumen.getCell(`D${totalRow}`).value = { formula: `SUM(D4:D${totalRow - 1})` }
	wsResumen.getCell(`E${totalRow}`).value = { formula: `SUM(E4:E${totalRow - 1})` }
	wsResumen.getCell(`C${totalRow}`).value = "Total"
	wsResumen.getRow(totalRow).font = { bold: true }

	const buffer = await wb.xlsx.writeBuffer()
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	})
	const url = URL.createObjectURL(blob)
	const a = document.createElement("a")
	a.href = url
	a.download = `portal-cautivo_por-zona_${fromDate}_${toDate}.xlsx`
	a.click()
	URL.revokeObjectURL(url)
}

function PortalCautivoPage() {
	const [fromDate, setFromDate] = React.useState("")
	const [toDate, setToDate] = React.useState("")
	const [zona, setZona] = React.useState<string | undefined>()
	const [consulting, setConsulting] = React.useState(false)
	const [stats, setStats] = React.useState<Stats | null>(null)
	const [downloading, setDownloading] = React.useState(false)
	const [downloadingPorZona, setDownloadingPorZona] = React.useState(false)
	const [progresoPorZona, setProgresoPorZona] = React.useState<{ done: number; total: number } | null>(null)
	const [apiError, setApiError] = React.useState<string | null>(null)

	const chartDiaRef = React.useRef<HTMLDivElement>(null)
	const chartEdadRef = React.useRef<HTMLDivElement>(null)
	const chartGeneroRef = React.useRef<HTMLDivElement>(null)

	// Refs y estado para renderizar gráficas ocultas por zona durante el export
	const offDiaRef = React.useRef<HTMLDivElement>(null)
	const offEdadRef = React.useRef<HTMLDivElement>(null)
	const offGeneroRef = React.useRef<HTMLDivElement>(null)
	const [offStats, setOffStats] = React.useState<Stats | null>(null)

	const prepareCharts = React.useCallback(async (s: Stats) => {
		flushSync(() => setOffStats(s))
		// Esperar dos frames para que recharts pinte
		await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())))
	}, [])

	const { data: zonas } = useQuery({
		queryKey: ["pc-zonas"],
		queryFn: async () => {
			const res = await fetch("/api/portal-cautivo/zonas", { credentials: "include" })
			return res.json() as Promise<{ item: string; municipio: string | null; nombre: string | null }[]>
		},
	})

	const handleConsultar = async () => {
		if (!fromDate || !toDate) return
		setConsulting(true)
		setApiError(null)
		try {
			const params = new URLSearchParams({ from: fromDate, to: toDate })
			if (zona && zona !== "all") params.set("zona", zona)
			const res = await fetch(`/api/portal-cautivo/stats?${params}`, { credentials: "include" })
			if (res.ok) {
				setStats(await res.json())
			} else {
				const body = await res.text()
				setApiError(`Error ${res.status}: ${body}`)
			}
		} catch (e) {
			setApiError(String(e))
		} finally {
			setConsulting(false)
		}
	}

	// Limpiar stats cuando cambian los filtros para que el Excel siempre corresponda a lo consultado
	const handleFromDateChange = (v: string) => { setFromDate(v); setStats(null) }
	const handleToDateChange = (v: string) => { setToDate(v); setStats(null) }
	const handleZonaChange = (v: string) => { setZona(v); setStats(null) }

	const handleDownload = async () => {
		if (!stats) return
		setDownloading(true)
		try {
			await downloadExcel(stats, fromDate, toDate, [chartDiaRef, chartEdadRef, chartGeneroRef])
		} finally {
			setDownloading(false)
		}
	}

	const handleDownloadPorZona = async () => {
		if (!fromDate || !toDate || !zonas || zonas.length === 0) return
		setDownloadingPorZona(true)
		setProgresoPorZona({ done: 0, total: zonas.length })
		setApiError(null)
		try {
			await downloadExcelPorZona(
				zonas,
				fromDate,
				toDate,
				prepareCharts,
				{ dia: offDiaRef, edad: offEdadRef, genero: offGeneroRef },
				(done, total) => setProgresoPorZona({ done, total }),
			)
		} catch (e) {
			setApiError(String(e))
		} finally {
			setDownloadingPorZona(false)
			setProgresoPorZona(null)
			setOffStats(null)
		}
	}

	const mostrarBotonPorZona = (!zona || zona === "all") && !!fromDate && !!toDate && Array.isArray(zonas) && zonas.length > 0

	return (
		<div className="p-6 space-y-6 bg-slate-100 min-h-screen">
			{/* HEADER */}
			<div className="flex items-center justify-between flex-wrap gap-3">
				<div>
					<h1 className="text-2xl font-bold">Portal Cautivo — Analytics</h1>
					<p className="text-gray-500 text-sm">Estadísticas de sesiones y usuarios por rango de fechas y zona</p>
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					{mostrarBotonPorZona && (
						<Button
							variant="outline"
							className="bg-blue-600 text-white hover:bg-blue-700 hover:text-white cursor-pointer"
							onClick={handleDownloadPorZona}
							disabled={downloadingPorZona}
						>
							{downloadingPorZona
								? `Generando... ${progresoPorZona?.done ?? 0}/${progresoPorZona?.total ?? 0}`
								: "Descargar Excel por zona"}
						</Button>
					)}
					{stats && (
						<Button
							variant="outline"
							className="bg-green-600 text-white hover:bg-green-700 hover:text-white cursor-pointer"
							onClick={handleDownload}
							disabled={downloading}
						>
							{downloading ? "Generando..." : "Descargar Excel"}
						</Button>
					)}
				</div>
			</div>

			{/* FILTROS */}
			<Card>
				<CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-6">
					<div>
						<Label>Desde</Label>
						<Input type="date" value={fromDate} onChange={(e) => handleFromDateChange(e.target.value)} />
					</div>
					<div>
						<Label>Hasta</Label>
						<Input type="date" value={toDate} onChange={(e) => handleToDateChange(e.target.value)} />
					</div>
					<div>
						<Label>Zona</Label>
						<Select value={zona} onValueChange={handleZonaChange}>
							<SelectTrigger>
								<SelectValue placeholder="Todas las zonas" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">Todas las zonas</SelectItem>
								{Array.isArray(zonas) && zonas.map((z) => (
									<SelectItem key={z.item} value={z.item}>
										{z.item}{z.municipio ? ` — ${z.municipio}` : ""}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="flex items-end">
						<Button
							className="w-full cursor-pointer"
							onClick={handleConsultar}
							disabled={!fromDate || !toDate || consulting}
						>
							{consulting ? "Consultando..." : "Consultar"}
						</Button>
					</div>
				</CardContent>
			</Card>

			{consulting && (
				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
					<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
					<span className="text-blue-700 font-medium">Cargando estadísticas...</span>
				</div>
			)}

			{apiError && (
				<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm font-mono whitespace-pre-wrap">
					{apiError}
				</div>
			)}

			{stats && (
				<>
					{/* TOTALES */}
					<section className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div className="bg-white p-4 rounded shadow">
							<div className="text-sm text-gray-500">Total Sesiones</div>
							<div className="text-3xl font-bold mt-2">{stats.totalSesiones.toLocaleString()}</div>
						</div>
						<div className="bg-white p-4 rounded shadow">
							<div className="text-sm text-gray-500">Total Usuarios</div>
							<div className="text-3xl font-bold mt-2">{stats.totalUsuarios.toLocaleString()}</div>
						</div>
					</section>

					{/* GRÁFICAS */}
					<section className="grid grid-cols-1 gap-6">

						{/* Sesiones y Usuarios por Día */}
						<Card>
							<CardHeader>
								<CardTitle>Sesiones y Usuarios por Día</CardTitle>
							</CardHeader>
							<CardContent>
								<div ref={chartDiaRef}>
									<ResponsiveContainer width="100%" height={300}>
										<LineChart data={stats.porDia} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis
												dataKey="fecha"
												tick={{ fontSize: 10 }}
												angle={-45}
												textAnchor="end"
												height={70}
											/>
											<YAxis tick={{ fontSize: 11 }} />
											<Tooltip
												formatter={(value: number, name: string) => [
													value.toLocaleString(),
													name === "sesiones" ? "Sesiones" : "Usuarios",
												]}
											/>
											<Legend
												formatter={(value) => value === "sesiones" ? "Sesiones" : "Usuarios"}
												wrapperStyle={{ paddingTop: 8 }}
											/>
											<Line
												type="monotone"
												dataKey="sesiones"
												stroke="#6366f1"
												strokeWidth={2}
												dot={false}
												activeDot={{ r: 4 }}
											/>
											<Line
												type="monotone"
												dataKey="usuarios"
												stroke="#10b981"
												strokeWidth={2}
												dot={false}
												activeDot={{ r: 4 }}
											/>
										</LineChart>
									</ResponsiveContainer>
								</div>
							</CardContent>
						</Card>

					</section>

					{/* EDADES Y GÉNEROS — dos columnas */}
					<section className="grid grid-cols-1 md:grid-cols-2 gap-6">

						{/* Edades */}
						<Card>
							<CardHeader>
								<CardTitle>Usuarios por Rango de Edad</CardTitle>
							</CardHeader>
							<CardContent>
								<div ref={chartEdadRef}>
									<ResponsiveContainer width="100%" height={300}>
										<BarChart data={stats.edades.map((d) => ({ name: d.categoria, cantidad: d.cantidad }))} margin={{ top: 5, right: 10, left: 0, bottom: 10 }}>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis dataKey="name" tick={{ fontSize: 12 }} />
											<YAxis tick={{ fontSize: 11 }} />
											<Tooltip formatter={(value: number) => [value.toLocaleString(), "Usuarios"]} />
											<Bar dataKey="cantidad" radius={[4, 4, 0, 0]}>
												{stats.edades.map((d, i) => (
													<Cell key={d.categoria} fill="#6366f1" fillOpacity={0.9 - i * 0.05} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</CardContent>
						</Card>

						{/* Géneros */}
						<Card>
							<CardHeader>
								<CardTitle>Usuarios por Género</CardTitle>
							</CardHeader>
							<CardContent>
								<div ref={chartGeneroRef}>
									<ResponsiveContainer width="100%" height={300}>
										<BarChart
											data={stats.generos.map((d) => ({
												name: ABBREV_MAP[d.categoria] ?? d.categoria,
												fullName: d.categoria,
												cantidad: d.cantidad,
											}))}
											margin={{ top: 5, right: 10, left: 0, bottom: 60 }}
										>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis
												dataKey="name"
												tick={{ fontSize: 10 }}
												angle={-35}
												textAnchor="end"
												height={70}
											/>
											<YAxis tick={{ fontSize: 11 }} />
											<Tooltip
												formatter={(value: number, _name, props) => [
													value.toLocaleString(),
													props.payload.fullName,
												]}
											/>
											<Bar dataKey="cantidad" radius={[4, 4, 0, 0]}>
												{stats.generos.map((d, i) => (
													<Cell key={d.categoria} fill="#10b981" fillOpacity={0.9 - i * 0.04} />
												))}
											</Bar>
										</BarChart>
									</ResponsiveContainer>
								</div>
							</CardContent>
						</Card>

					</section>
				</>
			)}

			{/* Charts ocultos para captura por zona durante el export */}
			<div
				aria-hidden
				style={{
					position: "fixed",
					left: -10000,
					top: 0,
					width: 560,
					background: "#ffffff",
					padding: 12,
					pointerEvents: "none",
				}}
			>
				{offStats && (
					<>
						<div ref={offDiaRef} style={{ width: 520, background: "#ffffff" }}>
							<LineChart
								width={520}
								height={240}
								data={offStats.porDia}
								margin={{ top: 5, right: 10, left: 0, bottom: 60 }}
							>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="fecha" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={70} />
								<YAxis tick={{ fontSize: 11 }} />
								<Legend
									formatter={(value) => value === "sesiones" ? "Sesiones" : "Usuarios"}
									wrapperStyle={{ paddingTop: 8 }}
								/>
								<Line type="monotone" dataKey="sesiones" stroke="#6366f1" strokeWidth={2} dot={false} isAnimationActive={false} />
								<Line type="monotone" dataKey="usuarios" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
							</LineChart>
						</div>
						<div ref={offEdadRef} style={{ width: 520, background: "#ffffff", marginTop: 12 }}>
							<BarChart
								width={520}
								height={240}
								data={offStats.edades.map((d) => ({ name: d.categoria, cantidad: d.cantidad }))}
								margin={{ top: 5, right: 10, left: 0, bottom: 10 }}
							>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="name" tick={{ fontSize: 12 }} />
								<YAxis tick={{ fontSize: 11 }} />
								<Bar dataKey="cantidad" radius={[4, 4, 0, 0]} isAnimationActive={false}>
									{offStats.edades.map((d, i) => (
										<Cell key={d.categoria} fill="#6366f1" fillOpacity={0.9 - i * 0.05} />
									))}
								</Bar>
							</BarChart>
						</div>
						<div ref={offGeneroRef} style={{ width: 520, background: "#ffffff", marginTop: 12 }}>
							<BarChart
								width={520}
								height={240}
								data={offStats.generos.map((d) => ({
									name: ABBREV_MAP[d.categoria] ?? d.categoria,
									cantidad: d.cantidad,
								}))}
								margin={{ top: 5, right: 10, left: 0, bottom: 60 }}
							>
								<CartesianGrid strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" height={70} />
								<YAxis tick={{ fontSize: 11 }} />
								<Bar dataKey="cantidad" radius={[4, 4, 0, 0]} isAnimationActive={false}>
									{offStats.generos.map((d, i) => (
										<Cell key={d.categoria} fill="#10b981" fillOpacity={0.9 - i * 0.04} />
									))}
								</Bar>
							</BarChart>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
